<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  historyWindows,
  serverInspectionResponse,
  type HistoryWindow,
  type ServerHistory,
  type ServerInspectionConnection,
  type ServerInspectionRequest,
  type ServerProcesses,
  type ServerResources,
} from '@atum-remote/protocol/server-inspection';
import type { RemoteDevice, RemoteUiAdapter } from '../model.js';
import { cpuUsage, serviceRows, journalRows, journalLevels } from '../lib/server-inspection.js';
import AppIcon from './AppIcon.vue';
import ResourcesView from './ResourcesView.vue';

const props = defineProps<{
  device: RemoteDevice;
  adapter?: RemoteUiAdapter | undefined;
  tab: 'resources' | 'logs';
  initialUnit?: string;
}>();
const emit = defineEmits<{ logs: [unit: string]; terminal: [] }>();
const connected = ref(false),
  busy = ref(false),
  error = ref(''),
  notice = ref(''),
  live = ref(true);
const resources = ref<ServerResources>(),
  previous = ref<ServerResources>(),
  cpu = ref<number | null>(null),
  history = ref<number[]>([]);
const services = ref<ReturnType<typeof serviceRows>>([]),
  processes = ref<ServerProcesses>();
const WINDOW_KEY = 'atum-remote.resources.window';
function storedWindow(): HistoryWindow {
  try {
    const value = localStorage.getItem(WINDOW_KEY);
    return historyWindows.find((w) => w === value) ?? '1h';
  } catch {
    return '1h';
  }
}
const historyWindow = ref<HistoryWindow>(storedWindow());
const recorded = ref<ServerHistory>(),
  /** Health findings always judge the fine 15-minute ring, whatever window is charted. */
  recent = ref<ServerHistory>();
let historyAt = 0,
  recentAt = 0,
  windowPending = false;
/** Coarser windows change slowly; refetch them less often than the live readings. */
const historyEvery: Record<HistoryWindow, number> = {
  '15m': 10_000,
  '1h': 30_000,
  '6h': 120_000,
  '24h': 300_000,
};
const entries = ref<ReturnType<typeof journalRows>['entries']>([]),
  search = ref('');
const unit = ref(props.initialUnit ?? ''),
  minutes = ref<15 | 60 | 1440>(60),
  priority = ref<'all' | 'warning' | 'error'>('all');
const sampledAt = ref<number>(),
  uid = ref<number>(),
  logWindow = ref('');
let connection: ServerInspectionConnection | undefined,
  generation = 0;
const available = computed(
  () => props.device[props.tab].available && !!props.adapter?.openInspection,
);
const filteredServices = computed(() =>
  services.value
    .filter((s) =>
      `${s.unit} ${s.description} ${s.active}`.toLowerCase().includes(search.value.toLowerCase()),
    )
    .sort(
      (a, b) =>
        Number(b.active === 'failed') - Number(a.active === 'failed') ||
        a.unit.localeCompare(b.unit),
    ),
);
const filteredEntries = computed(() =>
  entries.value.filter((e) =>
    `${e.unit} ${e.message}`.toLowerCase().includes(search.value.toLowerCase()),
  ),
);
async function disconnect() {
  generation++;
  const old = connection;
  connection = undefined;
  connected.value = false;
  busy.value = false;
  await old?.close();
}
async function refresh(connect = false) {
  if (busy.value || !available.value) return;
  busy.value = true;
  error.value = '';
  notice.value = '';
  const attempt = generation;
  try {
    if (connection && !connection.isOpen() && !connect)
      throw new Error('Server session ended. Reconnect to take a fresh reading.');
    if (connect || !connection?.isOpen()) {
      const old = connection;
      connection = undefined;
      await old?.close();
      const opened = await props.adapter!.openInspection!(props.device);
      if (attempt !== generation) {
        await opened.close();
        return;
      }
      connection = opened;
      connected.value = true;
      resources.value = undefined;
      previous.value = undefined;
      recorded.value = undefined;
      recent.value = undefined;
      historyAt = 0;
      recentAt = 0;
      cpu.value = null;
      history.value = [];
    }
    const current = connection!;
    const requests: ServerInspectionRequest[] =
      props.tab === 'resources'
        ? [{ section: 'resources' }, { section: 'processes' }, { section: 'services' }]
        : [
            {
              section: 'logs',
              ...(unit.value.trim() ? { unit: unit.value.trim() } : {}),
              minutes: minutes.value,
              priority: priority.value,
            },
          ];
    const warnings: string[] = [];
    for (const request of requests) {
      if (request.section === 'history') {
        // History is supplementary: a failure keeps the last chart and the live readings.
        try {
          const result = serverInspectionResponse.parse(await current.request(request));
          if (attempt !== generation) return;
          if (result.section === 'history' && result.window === '15m') {
            recent.value = result;
            recentAt = Date.now();
          }
          if (result.section === 'history' && result.window === historyWindow.value) {
            recorded.value = result;
            historyAt = Date.now();
          }
        } catch {
          if (attempt !== generation) return;
          if (request.window === '15m') recentAt = Date.now();
          if (request.window === historyWindow.value) historyAt = Date.now();
          warnings.push('history: the host did not return recorded history this time.');
          if (!current.isOpen())
            throw new Error('Server session ended. Reconnect to take a fresh reading.');
        }
        continue;
      }
      const result = serverInspectionResponse.parse(await current.request(request));
      if (attempt !== generation) return;
      if (result.section !== request.section) throw new Error('Unexpected server reading.');
      sampledAt.value = result.sampledAt;
      uid.value = result.executionUid;
      const output = result.section === 'resources' ? result.storage : result.output;
      const incomplete = output.truncated || output.output_incomplete;
      if (output.timed_out || !output.started)
        throw new Error('The server reading timed out. Refresh to try again.');
      // Structured process rows supersede the ps output kept for older clients.
      if (incomplete && !(result.section === 'processes' && result.processes))
        warnings.push(`${result.section}: the server returned a partial reading.`);
      if (output.stderr.trim()) warnings.push(output.stderr.trim());
      if (output.exit_code !== 0 && !output.stderr.trim())
        warnings.push(`${result.section}: unavailable to the enrolled account.`);
      if (result.section === 'resources') {
        cpu.value = cpuUsage(resources.value, result);
        if (cpu.value != null) history.value = [...history.value.slice(-35), cpu.value];
        previous.value = resources.value;
        resources.value = result;
        // Only hosts that advertise history are asked for it, in this same round.
        if (
          result.capabilities?.includes('history') &&
          (recorded.value?.window !== historyWindow.value ||
            Date.now() - historyAt >= historyEvery[historyWindow.value])
        )
          requests.push({ section: 'history', window: historyWindow.value });
        if (
          result.capabilities?.includes('history') &&
          historyWindow.value !== '15m' &&
          Date.now() - recentAt >= historyEvery['15m']
        )
          requests.push({ section: 'history', window: '15m' });
      } else if (result.section === 'services')
        services.value = serviceRows(output.stdout, incomplete);
      else if (result.section === 'processes') processes.value = result;
      else {
        if (request.section !== 'logs') throw new Error('Unexpected journal response.');
        const parsed = journalRows(output.stdout, incomplete);
        entries.value = parsed.entries;
        if (parsed.skipped)
          warnings.push(`${parsed.skipped} journal entries could not be displayed.`);
        logWindow.value = `${request.unit || 'All services'} · last ${request.minutes === 1440 ? '24 hours' : `${request.minutes} minutes`} · ${request.priority === 'all' ? 'all levels' : `${request.priority} and higher`}`;
      }
    }
    notice.value = warnings.join('\n');
  } catch (cause) {
    if (attempt !== generation) return;
    error.value = cause instanceof Error ? cause.message : 'Could not read this server.';
    connected.value = false;
    const old = connection;
    connection = undefined;
    await old?.close();
  } finally {
    if (attempt === generation) busy.value = false;
    // A window chosen mid-reading is fetched as soon as this reading completes.
    if (attempt === generation && windowPending && connected.value) {
      windowPending = false;
      void refresh();
    }
  }
}
watch(
  [() => props.device.id, () => props.tab, () => available.value],
  () => {
    void disconnect();
    resources.value = undefined;
    previous.value = undefined;
    recorded.value = undefined;
    recent.value = undefined;
    services.value = [];
    processes.value = undefined;
    entries.value = [];
    sampledAt.value = undefined;
    uid.value = undefined;
    search.value = '';
    error.value = '';
    notice.value = '';
    if (available.value) void refresh(true);
  },
  { immediate: true },
);
const timer = setInterval(() => {
  if (connected.value && connection && !connection.isOpen()) {
    void disconnect();
    return;
  }
  if (
    props.tab === 'resources' &&
    live.value &&
    connected.value &&
    document.visibilityState === 'visible'
  )
    void refresh();
}, 5000);
watch(historyWindow, (value) => {
  try {
    localStorage.setItem(WINDOW_KEY, value);
  } catch {
    // A remembered window is a convenience only.
  }
  if (!connected.value) return;
  if (busy.value) windowPending = true;
  else void refresh();
});
onBeforeUnmount(() => {
  clearInterval(timer);
  void disconnect();
});
</script>

<template>
  <section class="server-panel" :aria-busy="busy">
    <header class="panel server-toolbar">
      <div>
        <h2>
          <AppIcon :name="tab" />{{ tab === 'resources' ? 'Server resources' : 'System journal' }}
        </h2>
        <p>
          {{
            tab === 'resources'
              ? 'Current readings, recent history, storage, network, processes and services.'
              : 'Recent service and system events from this boot. Newest first.'
          }}
        </p>
      </div>
      <div class="server-actions">
        <label v-if="tab === 'resources'"
          ><input v-model="live" type="checkbox" /> Update every 5 seconds</label
        >
        <button :disabled="busy || !available" @click="refresh()">
          <AppIcon name="refresh" />{{ busy ? 'Reading…' : connected ? 'Refresh' : 'Reconnect' }}
        </button>
        <button :disabled="!device.terminal.available" @click="emit('terminal')">
          <AppIcon name="terminal" />Terminal
        </button>
      </div>
    </header>
    <p v-if="!available" class="panel server-empty">{{ device[tab].explanation }}</p>
    <p v-if="error" class="callout" role="alert">{{ error }}</p>
    <p v-if="notice" class="callout server-notice" role="status">{{ notice }}</p>
    <p v-if="sampledAt" class="server-reading" role="status">
      {{
        connected
          ? live && tab === 'resources'
            ? 'Updating'
            : 'Connected'
          : 'Disconnected · last reading retained'
      }}
      · Read {{ new Date(sampledAt).toLocaleTimeString() }} · Enrolled account UID {{ uid }}
    </p>
    <p v-else-if="busy" class="server-empty" role="status">
      Connecting to {{ device.name }} and reading server data…
    </p>
    <template v-if="tab === 'resources' && resources">
      <ResourcesView
        v-model:history-window="historyWindow"
        :resources="resources"
        :previous="previous"
        :processes="processes"
        :history="recorded"
        :recent-history="recent"
        :cpu="cpu"
        :session-cpu="history"
        :busy="busy"
      />
      <section class="panel server-section">
        <div class="server-section-title">
          <h3>
            Systemd services
            <span v-if="services.length" class="tag"
              >{{ services.filter((s) => s.active === 'failed').length }} failed</span
            >
          </h3>
          <label
            >Find a service<input
              v-model="search"
              type="search"
              placeholder="Name, description or state"
          /></label>
        </div>
        <div class="server-table">
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>State</th>
                <th>Description</th>
                <th><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in filteredServices" :key="s.unit">
                <td class="mono">{{ s.unit }}</td>
                <td>
                  <span class="tag" :class="{ 'server-failed': s.active === 'failed' }"
                    >{{ s.active }} · {{ s.sub }}</span
                  >
                </td>
                <td>{{ s.description }}</td>
                <td>
                  <button v-if="s.logsAvailable" class="small" @click="emit('logs', s.unit)">
                    View logs
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="!filteredServices.length && !busy">No matching services in this reading.</p>
      </section>
    </template>
    <template v-if="tab === 'logs'">
      <form class="panel server-filters" @submit.prevent="refresh()">
        <label
          >Service<input
            v-model="unit"
            placeholder="All services, or nginx.service"
            maxlength="188"
            pattern="[A-Za-z0-9][A-Za-z0-9_.@:\-]{0,179}\.service"
        /></label>
        <label
          >Time window<select v-model="minutes">
            <option :value="15">Last 15 minutes</option>
            <option :value="60">Last hour</option>
            <option :value="1440">Last 24 hours</option>
          </select></label
        >
        <label
          >Severity<select v-model="priority">
            <option value="all">All levels</option>
            <option value="warning">Warning and higher</option>
            <option value="error">Error and higher</option>
          </select></label
        >
        <button class="primary" :disabled="busy || !available">Apply filters</button>
      </form>
      <section class="panel server-section">
        <div class="server-section-title">
          <div>
            <h3>
              Recent entries
              <span class="tag">{{ filteredEntries.length }} of {{ entries.length }} shown</span>
            </h3>
            <p>{{ logWindow || 'Choose a service and time window.' }}</p>
          </div>
          <label
            >Search these entries<input
              v-model="search"
              type="search"
              placeholder="Message or service"
          /></label>
        </div>
        <p class="server-scope">
          Up to 200 entries, bounded by response size. Only journal entries readable by the enrolled
          account are included. Missing system logs may require an administrator to grant journal
          access.
        </p>
        <ol class="server-journal">
          <li v-for="(entry, index) in filteredEntries" :key="`${entry.at}-${index}`">
            <div>
              <time :datetime="entry.at">{{ new Date(entry.at).toLocaleString() }}</time
              ><span class="tag" :class="{ 'server-failed': entry.priority <= 3 }">{{
                journalLevels[entry.priority]
              }}</span
              ><span class="mono">{{ entry.unit }}</span>
            </div>
            <pre>{{ entry.message }}</pre>
          </li>
        </ol>
        <p v-if="sampledAt && !filteredEntries.length && !busy" class="server-empty">
          {{
            entries.length
              ? 'No entries match this search.'
              : notice
                ? 'No readable entries were returned. Review the access notice above.'
                : 'No readable journal entries in this time window.'
          }}
        </p>
      </section>
    </template>
  </section>
</template>

<style scoped>
.server-panel {
  display: grid;
  gap: 16px;
  min-width: 0;
}
.server-toolbar,
.server-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.server-toolbar,
.server-section,
.server-filters {
  padding: 22px;
  min-width: 0;
}
h2,
h3,
p {
  margin: 0;
}
h2 {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 18px;
}
h3 {
  font-size: 15px;
}
p {
  color: var(--text-muted);
  line-height: 1.6;
}
h2 + p,
h3 + p {
  margin-top: 6px;
}
.server-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.server-actions label {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 12px;
}
.server-actions input {
  width: auto;
}
.server-reading {
  font-size: 12px;
}
.server-notice {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.server-failed {
  color: var(--danger-text);
  background: var(--danger-wash);
}
.server-filters {
  display: flex;
  align-items: end;
  gap: 14px;
  flex-wrap: wrap;
}
.server-filters label,
.server-section-title label {
  display: grid;
  gap: 7px;
  font-size: 12px;
}
.server-filters label:first-child {
  flex: 1;
  min-width: 210px;
}
.server-filters input,
.server-filters select {
  width: 100%;
}
.server-scope {
  margin: 16px 0;
  font-size: 12px;
}
.server-journal {
  list-style: none;
  padding: 0;
  margin: 0;
}
.server-journal li {
  padding: 16px 0;
  border-top: 1px solid var(--line);
}
.server-journal li > div {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  font-size: 11px;
  color: var(--text-muted);
}
.server-journal pre {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 12px;
  line-height: 1.7;
  margin: 10px 0 0;
}
.server-empty {
  padding: 24px;
  text-align: center;
}
@media (max-width: 600px) {
  .server-toolbar,
  .server-section,
  .server-filters {
    padding: 16px;
  }
  .server-filters label {
    width: 100%;
  }
}
</style>
